import { PaymentWebsocket } from '@cibli/shared';
import { useEffect, useState, useCallback } from 'react';

import { paymentWebSocket } from '../services/payment.websocket';

export interface UsePaymentWebSocketReturn {
  isConnected: boolean;
  readerStates: Map<string, PaymentWebsocket.ReaderStateUpdate>;
  paymentIntents: Map<string, PaymentWebsocket.PaymentIntentUpdate>;
  subscribedReaders: string[];
  subscribedPaymentIntents: string[];
  subscriptionResponses: Map<string, PaymentWebsocket.SubscriptionResponseData>;
  subscriptionUpdates: Map<string, PaymentWebsocket.SubscriptionUpdateData>;
  connect: () => void;
  disconnect: () => void;
  subscribeToReader: (readerId: string) => void;
  unsubscribeFromReader: (readerId: string) => void;
  isSubscribedToReader: (readerId: string) => boolean;
  subscribeToPaymentIntent: (paymentIntentId: string) => void;
  unsubscribeFromPaymentIntent: (paymentIntentId: string) => void;
  isSubscribedToPaymentIntent: (paymentIntentId: string) => boolean;
}

export function usePaymentWebSocket(): UsePaymentWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [readerStates, setReaderStates] = useState<Map<string, PaymentWebsocket.ReaderStateUpdate>>(
    new Map(),
  );
  const [paymentIntents, setPaymentIntents] = useState<
    Map<string, PaymentWebsocket.PaymentIntentUpdate>
  >(new Map());
  const [subscribedReaders, setSubscribedReaders] = useState<string[]>([]);
  const [subscribedPaymentIntents, setSubscribedPaymentIntents] = useState<string[]>([]);
  const [subscriptionResponses, setSubscriptionResponses] = useState<
    Map<string, PaymentWebsocket.SubscriptionResponseData>
  >(new Map());
  const [subscriptionUpdates, setSubscriptionUpdates] = useState<
    Map<string, PaymentWebsocket.SubscriptionUpdateData>
  >(new Map());

  const handleReaderStateUpdate = useCallback((data: PaymentWebsocket.PaymentWebSocketMessage) => {
    const update = data as PaymentWebsocket.ReaderStateUpdate;
    setReaderStates(prev => new Map(prev).set(update.readerId, update));
  }, []);

  const handlePaymentIntentUpdate = useCallback(
    (data: PaymentWebsocket.PaymentWebSocketMessage) => {
      const update = data as PaymentWebsocket.PaymentIntentUpdate;
      setPaymentIntents(prev => new Map(prev).set(update.paymentIntentId, update));
    },
    [],
  );

  const handleSubscriptionResponse = useCallback(
    (data: PaymentWebsocket.PaymentWebSocketMessage) => {
      const response = data as PaymentWebsocket.SubscriptionResponseData;
      if (response.readerId) {
        setSubscriptionResponses(prev => new Map(prev).set(response.readerId!, response));
      }
      if (response.paymentIntentId) {
        setSubscriptionResponses(prev => new Map(prev).set(response.paymentIntentId!, response));
      }
    },
    [],
  );

  const handleSubscriptionUpdate = useCallback((data: PaymentWebsocket.PaymentWebSocketMessage) => {
    const update = data as PaymentWebsocket.SubscriptionUpdateData;
    if (update.readerId) {
      setSubscriptionUpdates(prev => new Map(prev).set(update.readerId!, update));

      // Update subscribed readers list
      setSubscribedReaders(prev => {
        if (update.subscribed) {
          return prev.includes(update.readerId!) ? prev : [...prev, update.readerId!];
        } else {
          return prev.filter(id => id !== update.readerId);
        }
      });
    }

    if (update.paymentIntentId) {
      setSubscriptionUpdates(prev => new Map(prev).set(update.paymentIntentId!, update));

      // Update subscribed payment intents list
      setSubscribedPaymentIntents(prev => {
        if (update.subscribed) {
          return prev.includes(update.paymentIntentId!) ? prev : [...prev, update.paymentIntentId!];
        } else {
          return prev.filter(id => id !== update.paymentIntentId);
        }
      });
    }
  }, []);

  const connect = useCallback(() => {
    paymentWebSocket.connect();
  }, []);

  const disconnect = useCallback(() => {
    paymentWebSocket.disconnect();
  }, []);

  const subscribeToReader = useCallback((readerId: string) => {
    paymentWebSocket.subscribeToReader(readerId);
    // Update local state immediately
    setSubscribedReaders(prev => (prev.includes(readerId) ? prev : [...prev, readerId]));
  }, []);

  const unsubscribeFromReader = useCallback((readerId: string) => {
    paymentWebSocket.unsubscribeFromReader(readerId);
    // Update local state immediately
    setSubscribedReaders(prev => prev.filter(id => id !== readerId));
  }, []);

  const isSubscribedToReader = useCallback((readerId: string) => {
    return paymentWebSocket.isSubscribedToReader(readerId);
  }, []);

  const subscribeToPaymentIntent = useCallback(async (paymentIntentId: string): Promise<void> => {
    await paymentWebSocket.subscribeToPaymentIntent(paymentIntentId);
    // Update local state after confirmation
    setSubscribedPaymentIntents(prev =>
      prev.includes(paymentIntentId) ? prev : [...prev, paymentIntentId],
    );
  }, []);

  const unsubscribeFromPaymentIntent = useCallback((paymentIntentId: string) => {
    paymentWebSocket.unsubscribeFromPaymentIntent(paymentIntentId);
    // Update local state immediately
    setSubscribedPaymentIntents(prev => prev.filter(id => id !== paymentIntentId));
  }, []);

  const isSubscribedToPaymentIntent = useCallback((paymentIntentId: string) => {
    return paymentWebSocket.isSubscribedToPaymentIntent(paymentIntentId);
  }, []);

  useEffect(() => {
    // Set up event listeners
    paymentWebSocket.on('reader_state_update', handleReaderStateUpdate);
    paymentWebSocket.on('payment_intent_update', handlePaymentIntentUpdate);
    paymentWebSocket.on('subscription_response', handleSubscriptionResponse);
    paymentWebSocket.on('subscription_update', handleSubscriptionUpdate);

    // Check initial connection status
    setIsConnected(paymentWebSocket.isConnected());

    // Connect if not already connected
    if (!paymentWebSocket.isConnected()) {
      connect();
    }

    // Set up connection status monitoring
    const checkConnection = () => {
      setIsConnected(paymentWebSocket.isConnected());
    };

    const interval = setInterval(checkConnection, 1000);

    return () => {
      paymentWebSocket.off('reader_state_update', handleReaderStateUpdate);
      paymentWebSocket.off('payment_intent_update', handlePaymentIntentUpdate);
      paymentWebSocket.off('subscription_response', handleSubscriptionResponse);
      paymentWebSocket.off('subscription_update', handleSubscriptionUpdate);
      clearInterval(interval);
    };
  }, [
    handleReaderStateUpdate,
    handlePaymentIntentUpdate,
    handleSubscriptionResponse,
    handleSubscriptionUpdate,
    connect,
  ]);

  return {
    isConnected,
    readerStates,
    paymentIntents,
    subscribedReaders,
    subscribedPaymentIntents,
    subscriptionResponses,
    subscriptionUpdates,
    connect,
    disconnect,
    subscribeToReader,
    unsubscribeFromReader,
    isSubscribedToReader,
    subscribeToPaymentIntent,
    unsubscribeFromPaymentIntent,
    isSubscribedToPaymentIntent,
  };
}

// Hook to get reader state for a specific reader
export function useReaderState(readerId: string) {
  const { readerStates } = usePaymentWebSocket();
  return readerStates.get(readerId);
}

// Hook to get payment intent state for a specific payment intent
export function usePaymentIntentState(paymentIntentId: string) {
  const { paymentIntents } = usePaymentWebSocket();
  return paymentIntents.get(paymentIntentId);
}
